import React, { useRef } from 'react';
import { formatTime } from '../utils';
import type { QuestionItem, AnalysisItem, ChatMessage, PromptAction, AgentActionName, HintSession } from '../types';
import MarkdownRenderer from './MarkdownRenderer';
import aiAvatar from '../assets/line-husky-nobackground.png';
import {
  HierarchyIcon,
  SparkleIcon,
  ThumbsUpIcon,
  NotebookIcon,
  PaperclipIcon,
  ImageIcon,
  ArrowUpRightIcon,
  CompassIcon,
} from './icons';

const promptActions: PromptAction[] = [
  { key: 'summarize_mistake', label: '归纳错因', prompt: '请帮我归纳这道题的错因，并给出下次避免的方法。', icon: <CompassIcon /> },
  { key: 'explain_differently', label: '换种讲法', prompt: '请换一种更直观的方式讲解这道题。', icon: <SparkleIcon /> },
  { key: 'hint', label: '分级提示', prompt: '', icon: <NotebookIcon /> },
  { key: 'similar', label: '生成相似题', prompt: '', icon: <SparkleIcon /> },
];

interface ReasoningCenterProps {
  hintSession?: HintSession;
  onHint?: () => void;
  onReturnToAnswer?: () => void;
  onRevealAnalysis?: () => void;
  onSaveExcerpt?: (message: ChatMessage) => void;
  question: QuestionItem | null;
  analysis: AnalysisItem | null;
  messages: ChatMessage[];
  reply: string;
  setReply: (value: string) => void;
  onSendMessage: () => void;
  onRetryMessage: (message: ChatMessage) => void;
  onReanalyze: () => void;
  onGenerateLearningState: () => void;
  onAgentAction: (action: AgentActionName, params?: Record<string, unknown>) => void;
  onConfirmProposal: () => void;
  onRejectProposal: () => void;
  activeProposalId: string | null;
  attachments: File[];
  onAddAttachments: (files: File[]) => void;
  onRemoveAttachment: (index: number) => void;
  reanalyzing: boolean;
  sendingMessage: boolean;
  generatingLearning: boolean;
  agentActionPending: boolean;
}

export default function ReasoningCenter({
  hintSession,
  onHint,
  onReturnToAnswer,
  onRevealAnalysis,
  onSaveExcerpt,
  question,
  analysis,
  messages,
  reply,
  setReply,
  onSendMessage,
  onRetryMessage,
  onReanalyze,
  onGenerateLearningState,
  onAgentAction,
  onConfirmProposal,
  onRejectProposal,
  activeProposalId,
  attachments,
  onAddAttachments,
  onRemoveAttachment,
  reanalyzing,
  sendingMessage,
  generatingLearning,
  agentActionPending,
}: ReasoningCenterProps) {
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const waitingForOCR = !!question && !question.stem.trim() && ['queued', 'processing', 'pending'].includes(question.ocrStatus);
  const manualAnalysisPending = question?.parseSource === 'manual_corrected' && question.analysisStatus === 'pending';

  function handleAttachmentChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    onAddAttachments(files);
    event.target.value = '';
  }

  function applyPromptAction(action: PromptAction) {
    if (action.key === 'summarize_mistake') {
      onGenerateLearningState();
      return;
    }
    if (action.key === 'explain_differently') {
      onAgentAction('explain_alternative', { focus: '当前不理解的概念或步骤' });
      return;
    }
    if (action.key === 'hint') {
      if (onHint) onHint(); else onAgentAction('hint', { hintLevel: 1 });
      return;
    }
    if (action.key === 'similar') {
      onAgentAction('generate_similar_question', {});
      return;
    }
    setReply(action.prompt);
    composerRef.current?.focus();
  }

  return (
    <section className="panel panel--center">
      <div className="center-layout">
        <header className="section-header">
          <div className="section-title">
            <HierarchyIcon size={20} />
            <h1>{question ? question.stem.slice(0, 18) : '题目工作台'}</h1>
          </div>
          <div className="section-meta">{question ? `ID: ${question.id}` : '未选择题目'}</div>
        </header>

        <div className="conversation-scroll">
          <div className="conversation-stack">
            {hintSession?.active ? <article className="hint-card" aria-label="分级提示">
              <h3>分级提示 · {hintSession.hints.length}/3</h3>
              <p className="section-meta">答案、解析、历史对话及笔记已收起。可逐级思考，或主动查看完整解析。</p>
              {hintSession.hints.map(hint => <section key={hint.level}><h4>第 {hint.level} 级 · {['知识点与方向', '关键推导与步骤', '具体引导'][hint.level - 1]}</h4><MarkdownRenderer content={hint.text} /><p>{hint.nextQuestion}</p></section>)}
              {hintSession.pending ? <p role="status">正在获取第 {hintSession.hints.length + 1} 级提示…</p> : null}
              {hintSession.error ? <p role="alert">{hintSession.error}</p> : null}
              <div className="note-actions">
                {hintSession.hints.length < 3 ? <button type="button" className="filled-button" disabled={hintSession.pending} onClick={onHint}>{hintSession.error ? '重试当前提示' : '继续提示'}</button> : <span>三级提示已完成，试着完成最后一步。</span>}
                <button type="button" className="text-button" onClick={onReturnToAnswer}>返回作答</button>
                <button type="button" className="text-button" onClick={onRevealAnalysis}>查看完整解析</button>
              </div>
            </article> : null}
            {!hintSession?.active && analysis ? (
              <article className="analysis-card">
                <div className="analysis-card-header">
                  <div className="analysis-card-title">
                    <SparkleIcon size={18} />
                    <span>AI 解析</span>
                  </div>
                  <span className="analysis-card-time">{formatTime(analysis.createdAt)}</span>
                </div>
                <div className="analysis-card-copy">
                  {analysis.isStale || question?.analysisStale ? <p className="structure-warning-panel">历史解析：题面已校准，需重新解析后确认结论。</p> : null}
                  <strong>答案：</strong>
                  <div className="analysis-answer-content">
                    <MarkdownRenderer content={analysis.content.answer || analysis.answer || '暂无'} />
                  </div>
                  <p>{analysis.content.summary || '暂无解析摘要'}</p>
                  {question?.categoryId && analysis.content.taxonomySuggestion && (analysis.content.taxonomySuggestion.categoryName || analysis.content.taxonomySuggestion.tagNames?.length) ? (
                    <div className="taxonomy-suggestion" aria-label="分类与标签">
                      <span>标签：</span>
                      {analysis.content.taxonomySuggestion.categoryName ? <strong>{analysis.content.taxonomySuggestion.categoryName}</strong> : null}
                      {analysis.content.taxonomySuggestion.tagNames?.map((tag) => <em key={tag}>{tag}</em>)}
                    </div>
                  ) : null}
                </div>
                <div className="analysis-card-actions">
                  <button className="text-button is-highlight" type="button" onClick={onReanalyze} disabled={reanalyzing}>
                    <SparkleIcon size={12} />
                    {reanalyzing ? '解析中...' : '重新解析'}
                  </button>
                  <button
                    className="text-button"
                    type="button"
                    onClick={() => {
                      composerRef.current?.focus();
                    }}
                  >
                    <ThumbsUpIcon size={14} />
                    发送追问
                  </button>
                </div>
              </article>
            ) : null}
            {!hintSession?.active && question && !analysis && question.ocrStatus === 'failed' ? (
              <article className="analysis-card" role="alert">
                <div className="analysis-card-title">识别失败</div>
                <p>重试识别或编辑校准</p>
              </article>
            ) : null}
            {!hintSession?.active && question && !analysis && manualAnalysisPending ? (
              <article className="analysis-card" role="status">
                <div className="analysis-card-title">待解析</div>
                <button className="text-button" type="button" onClick={onReanalyze} disabled={reanalyzing}>生成解析</button>
              </article>
            ) : null}
            {!hintSession?.active && question && !analysis && !manualAnalysisPending && question.ocrStatus !== 'failed' && ['queued', 'processing', 'pending'].includes(question.analysisStatus) ? (
              <article className="analysis-card analysis-card--pending" role="status">
                <div className="analysis-card-title"><SparkleIcon size={18} /><span>{waitingForOCR ? '识别中' : '解析中'}</span></div>
              </article>
            ) : null}

            {(!hintSession?.active ? messages.filter(message => message.questionId === question?.id) : []).map((message) =>
              message.role === 'user' ? (
                <div key={message.id} className={`user-message${message.clientStatus === 'failed' ? ' is-failed' : ''}`}>
                  <div className="user-bubble">{message.message}</div>
                  <div className="message-meta">
                    <span className="section-meta">{formatTime(message.createdAt)}</span>
                    {message.clientStatus === 'pending' ? <span className="message-status">发送中...</span> : null}
                    {message.clientStatus === 'failed' ? (
                      <>
                        <span className="message-status is-error">发送失败</span>
                        <button className="text-button message-retry-button" type="button" onClick={() => onRetryMessage(message)}>
                          重新发送
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div key={message.id} className={`ai-message${message.clientStatus === 'pending' ? ' is-pending' : ''}`}>
                  <div className="ai-message-row">
                    <div className="ai-badge" aria-label="AI 助手">
                      <img src={aiAvatar} alt="" />
                    </div>
                    <div className="ai-bubble">
                      {message.clientStatus === 'pending' ? (
                        <div className="thinking-indicator" aria-live="polite">
                          <span>正在思考</span>
                          <i />
                          <i />
                          <i />
                        </div>
                      ) : (
                        <div className="explanation-block">
                          <MarkdownRenderer content={message.message} />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="message-meta">
                    <span className="section-meta">{formatTime(message.createdAt)}</span>
                    {message.role === 'assistant' && !message.clientStatus && onSaveExcerpt ? <button type="button" className="text-button" onClick={() => onSaveExcerpt(message)}>存为本题笔记</button> : null}
                  </div>
                </div>
              ),
            )}
            {!hintSession?.active && activeProposalId ? (
              <div className="taxonomy-suggestion" aria-label="AI 候选确认">
                <span>候选已保存，确认后才会进入题库</span>
                <button className="text-button is-highlight" type="button" onClick={onConfirmProposal}>确认保存</button>
                <button className="text-button" type="button" onClick={onRejectProposal}>放弃</button>
              </div>
            ) : null}
          </div>
        </div>

        <footer className="prompt-area">
          <div className="chip-row agent-action-row">
            {promptActions.map((action) => (
              <button
                key={action.key}
                className="chip"
                type="button"
                onClick={() => applyPromptAction(action)}
                disabled={!question || agentActionPending || (hintSession?.active && action.key !== 'hint') || (action.key === 'summarize_mistake' && generatingLearning)}
              >
                {action.icon}
                <span>{agentActionPending && action.key !== 'summarize_mistake' ? '执行中...' : action.key === 'summarize_mistake' && generatingLearning ? '归纳中...' : action.label}</span>
              </button>
            ))}
          </div>
          {!hintSession?.active ? <div className="composer-card">
            <input
              ref={fileInputRef}
              className="sr-only"
              type="file"
              accept="image/*"
              multiple
              onChange={handleAttachmentChange}
            />
            <textarea
              ref={composerRef}
              className="composer-input composer-textarea"
              placeholder="输入追问内容..."
              value={reply}
              onChange={(event) => setReply(event.target.value)}
            />
            {attachments.length > 0 ? (
              <div className="attachment-list">
                {attachments.map((file, index) => (
                  <div className="attachment-item" key={`${file.name}-${index}`}>
                    <ImageIcon size={13} />
                    <span>{file.name}</span>
                    <button type="button" aria-label={`移除 ${file.name}`} onClick={() => onRemoveAttachment(index)}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            <div className="composer-toolbar">
              <div className="toolbar-left">
                <button
                  className="toolbar-button"
                  type="button"
                  aria-label="附件"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <PaperclipIcon size={14} />
                </button>
                <button
                  className="toolbar-button"
                  type="button"
                  aria-label="图片"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <ImageIcon size={14} />
                </button>
              </div>
              <button className="send-button" type="button" onClick={onSendMessage} disabled={sendingMessage || (!reply.trim() && attachments.length === 0)}>
                {sendingMessage ? '等待中' : '发送'}
                <ArrowUpRightIcon size={12} />
              </button>
            </div>
          </div> : null}
        </footer>
      </div>
    </section>
  );
}
